import React from 'react';
import { WorkflowStage } from '../types';
import { CheckCircle, Circle, XCircle, Clock, ArrowRight } from 'lucide-react';

interface WorkflowProgressTrackerProps {
  stages: WorkflowStage[];
}

const WorkflowProgressTracker: React.FC<WorkflowProgressTrackerProps> = ({ stages }) => {
  const getStageIcon = (status: WorkflowStage['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'in-progress':
        return <Clock className="w-6 h-6 text-blue-600 animate-pulse" />;
      case 'rejected':
        return <XCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Circle className="w-6 h-6 text-gray-300" />;
    }
  };

  // SIMPLIFIED: No more contract substages grouping - only 2 main stages now
  const displayStages = stages;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Workflow Progress</h2>
      
      {/* Horizontal Progress Layout */}
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          {displayStages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              {/* Stage Item */}
              <div className="flex-1 flex flex-col items-center min-w-0">
                {/* Stage Icon */}
                <div className="relative z-10 flex-shrink-0 mb-3">
                  {getStageIcon(stage.status)}
                </div>

                {/* Stage Name */}
                <h3 className={`text-sm font-semibold text-center mb-2 px-2 ${
                  stage.status === 'completed' ? 'text-green-900' :
                  stage.status === 'in-progress' ? 'text-blue-900' :
                  stage.status === 'rejected' ? 'text-red-900' : 'text-gray-900'
                }`}>
                  {stage.name}
                </h3>
                
                {/* Completion Date */}
                {stage.completedAt && (
                  <p className="text-xs text-gray-500 text-center mb-2">
                    {new Date(stage.completedAt).toLocaleDateString()}
                  </p>
                )}

                {/* Status Badge */}
                <div className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    stage.status === 'completed' ? 'bg-green-100 text-green-800' :
                    stage.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                    stage.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {stage.status === 'pending' ? 'Pending' :
                     stage.status === 'in-progress' ? 'In Progress' :
                     stage.status === 'completed' ? 'Completed' :
                     'Rejected'}
                  </span>
                </div>
              </div>

              {/* Arrow Connector (except after last stage) */}
              {index < displayStages.length - 1 && (
                <div className="flex items-center justify-center pt-3">
                  <ArrowRight className={`w-5 h-5 ${
                    stage.status === 'completed' ? 'text-green-600' : 'text-gray-300'
                  }`} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Progress Line */}
        <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-200" style={{ zIndex: 0 }}>
          <div
            className="bg-blue-600 h-0.5 transition-all duration-500"
            style={{
              width: `${(stages.filter(s => s.status === 'completed').length / stages.length) * 100}%`
            }}
          ></div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
          <span className="text-sm font-medium text-gray-900">
            {Math.round((stages.filter(s => s.status === 'completed').length / stages.length) * 100)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
            style={{
              width: `${(stages.filter(s => s.status === 'completed').length / stages.length) * 100}%`
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowProgressTracker;